import React, { useState } from 'react';

import {
  MainCard,
  PERMISSION_ACTIONS,
  PERMISSION_VERBS,
  PermissionsButtonWrapper,
  RestrictedPermissions,
  Text,
} from '~/components/core';
import Button from '~/components/core/Atomic/Buttons/Button';
import { AddIcon } from '~/components/deprecatedMuiIcons';
import { isPolicyManuallyFilled } from '~/Utils';
import type { PopulatedClaim } from '~/Utils/populateClaim';

import AddUpdateCapacityAgreementDialog from './CapacityAgreementEditables/AddUpdateCapacityAgreementDialog';
import EditCapacitySlotDialog from './CapacityAgreementEditables/EditCapacitySlotDialog';
import CapacityAgreementCard from './CapacityAgreementCard';
import { TEXTS } from './texts';
import type { CapacityAgreementType, CapacitySlotType } from './types';

interface CapacityAgreementsProps {
  claim: PopulatedClaim;
  organizationId: number;
  onUpdateSuccess?: () => Promise<void> | void;
}

interface CapacityAgreementsInnerProps extends CapacityAgreementsProps {
  isManualPolicy: boolean;
}

type AddUpdateCapacityAgreementDialogState = {
  open: boolean;
  capacityAgreement: CapacityAgreementType | null;
};

type EditCapacitySlotDialogState =
  | {
      open: true;
      capacitySlot: CapacitySlotType;
      capacityAgreementExternalId: string;
    }
  | {
      open: false;
      capacitySlot: null;
      capacityAgreementExternalId: null;
    };

function CapacityAgreementsInner({
  claim,
  organizationId,
  onUpdateSuccess,
  isManualPolicy,
}: CapacityAgreementsInnerProps) {
  const [addUpdateCapacityAgreementDialogState, setAddUpdateCapacityAgreementDialogState] =
    useState<AddUpdateCapacityAgreementDialogState>({
      open: false,
      capacityAgreement: null,
    });
  const [editCapacitySlotDialogState, setEditCapacitySlotDialogState] = useState<EditCapacitySlotDialogState>({
    open: false,
    capacitySlot: null,
    capacityAgreementExternalId: null,
  });
  const capacityAgreements = claim.policy.capacity_agreements ?? [];

  const openAddDialog = () => setAddUpdateCapacityAgreementDialogState({ open: true, capacityAgreement: null });

  const openEditSlotDialog = (capacitySlot: CapacitySlotType, capacityAgreementExternalId: string) =>
    setEditCapacitySlotDialogState({ open: true, capacitySlot, capacityAgreementExternalId });

  const title = (
    <div className="flex items-center justify-between">
      <Text variant={Text.VARIANTS.LG} weight={Text.WEIGHTS.SEMI_BOLD}>
        {TEXTS.sectionTitle()}
      </Text>
      <PermissionsButtonWrapper>
        <Button variant="outlined" color="primary" startIcon={<AddIcon />} onClick={openAddDialog}>
          <span className="mt-2 uppercase">{TEXTS.add}</span>
        </Button>
      </PermissionsButtonWrapper>
    </div>
  );

  // Here to appease the typescript lords
  if (!claim.policy.id) {
    return null;
  }

  return (
    <MainCard type="filled" title={title} className="bg-white">
      <div className="grid gap-24">
        {capacityAgreements.length > 0 ? (
          capacityAgreements.map((agreement) => {
            const onEditSlotClick = (capacitySlot: CapacitySlotType) =>
              openEditSlotDialog(capacitySlot, agreement.external_id);
            return (
              <CapacityAgreementCard
                key={agreement.id}
                agreement={agreement}
                onEditClick={() =>
                  setAddUpdateCapacityAgreementDialogState({ open: true, capacityAgreement: agreement })
                }
                onEditSlotClick={onEditSlotClick}
              />
            );
          })
        ) : (
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
            {TEXTS.noCapacityAgreements()}
          </Text>
        )}
      </div>
      {addUpdateCapacityAgreementDialogState.open ? (
        <AddUpdateCapacityAgreementDialog
          open
          onClose={() => setAddUpdateCapacityAgreementDialogState({ open: false, capacityAgreement: null })}
          onUpdateSuccess={onUpdateSuccess}
          organizationId={organizationId}
          policyId={claim.policy.id}
          isManualPolicy={isManualPolicy}
          capacityAgreement={addUpdateCapacityAgreementDialogState.capacityAgreement}
        />
      ) : null}
      {editCapacitySlotDialogState.open ? (
        <EditCapacitySlotDialog
          open
          onClose={() =>
            setEditCapacitySlotDialogState({ open: false, capacitySlot: null, capacityAgreementExternalId: null })
          }
          onUpdateSuccess={onUpdateSuccess}
          organizationId={organizationId}
          policyId={claim.policy.id}
          isManualPolicy={isManualPolicy}
          capacitySlot={editCapacitySlotDialogState.capacitySlot}
          capacityAgreementExternalId={editCapacitySlotDialogState.capacityAgreementExternalId}
        />
      ) : null}
    </MainCard>
  );
}

const CapacityAgreements = ({ claim, organizationId, onUpdateSuccess }: CapacityAgreementsProps) => {
  const isManualPolicy = isPolicyManuallyFilled(claim.policy);

  return (
    <RestrictedPermissions
      action={isManualPolicy ? PERMISSION_ACTIONS.MANUAL_POLICY : PERMISSION_ACTIONS.API_POLICY}
      verb={PERMISSION_VERBS.WRITE}
    >
      <CapacityAgreementsInner
        claim={claim}
        organizationId={organizationId}
        onUpdateSuccess={onUpdateSuccess}
        isManualPolicy={isManualPolicy}
      />
    </RestrictedPermissions>
  );
};

export default CapacityAgreements;
